/**
 * Zentraler API-Client (Phase 0.7).
 *
 * Basis-URL und Token-Provider werden vom Plattform-Boot injiziert:
 * Web = '' (Aufrufe bleiben byte-identisch relativ), native App später
 * = 'https://www.die-sizilianer.de'. Relative fetch('/api/…')-Aufrufe
 * waren der größte stille Bruchpunkt für React Native — sie fallen dort
 * zur LAUFZEIT um, nicht beim Kompilieren.
 *
 * Ersetzt außerdem den 14× wörtlich duplizierten
 * getSession→access_token→Authorization-Block (apiFetchAuthed).
 */

export type ApiFetchConfig = {
  baseUrl: string;
  /** Bearer-Token der aktuellen Session oder null (nicht eingeloggt). */
  getToken: () => Promise<string | null>;
};

let config: ApiFetchConfig | null = null;

export function configureApiFetch(cfg: ApiFetchConfig): void {
  config = cfg;
}

function requireConfig(): ApiFetchConfig {
  if (!config) {
    throw new Error(
      'apiFetch: nicht konfiguriert — Boot-Reihenfolge prüfen (configureApiFetch fehlt).',
    );
  }
  return config;
}

/**
 * fetch gegen die eigene API. Hängt das Bearer-Token an, WENN eine
 * Session existiert (öffentliche Endpoints ignorieren den Header).
 * Ein bereits gesetzter Authorization-Header wird nie überschrieben.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const cfg = requireConfig();
  const headers = new Headers(init.headers);
  if (!headers.has('Authorization')) {
    const token = await cfg.getToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(`${cfg.baseUrl}${path}`, { ...init, headers });
}

/**
 * Wie apiFetch, aber Login ist Pflicht — wirft mit der etablierten
 * deutschen Meldung, statt einen 401 vom Server zu provozieren.
 */
export async function apiFetchAuthed(path: string, init: RequestInit = {}): Promise<Response> {
  const cfg = requireConfig();
  const token = await cfg.getToken();
  if (!token) throw new Error('Nicht angemeldet — bitte neu einloggen.');
  const headers = new Headers(init.headers);
  if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
  return fetch(`${cfg.baseUrl}${path}`, { ...init, headers });
}

/**
 * Ein Fehler von unserer eigenen API — mit dem HTTP-Status als Zahl.
 *
 * ⚠️ Bis 18.08. stand der Status **nur im Meldungstext** (`"utilization 403"`).
 * Ihn per Regex wieder herauszuziehen bräche still, sobald jemand den Text
 * ändert — und Meldungstexte ändert man leichtfertig. Deshalb eine eigene
 * Eigenschaft. Der Text bleibt dabei byte-identisch, damit sich an dem, was
 * ein Mensch zu sehen bekommt, nichts ändert.
 *
 * Verhältnis zur Fehlertext-Regel: Die betrifft, was der **Server** nach außen
 * schreibt (`api/_lib/publicFehlertext.ts`). Hier steht eine Zahl in einem
 * Browser-Objekt, die derselbe Browser gerade empfangen hat — nichts verlässt
 * das Gerät.
 */
export class ApiFehler extends Error {
  readonly status: number;

  constructor(nachricht: string, status: number) {
    super(nachricht);
    this.name = 'ApiFehler';
    this.status = status;
  }
}

/**
 * Der HTTP-Status eines Fehlers, oder `null`, wenn keiner erkennbar ist.
 *
 * Der zweite Zweig ist ein bewusster Ausschlag über unsere eigenen Endpunkte
 * hinaus: Supabase' `AuthError` trägt ebenfalls ein numerisches `status`, und
 * ein 403 von dort ist genauso wenig durch Wiederholen zu heilen. Wer null
 * Ausschlag will, streicht den Zweig — dann trägt nur `ApiFehler`.
 */
export function apiStatus(fehler: unknown): number | null {
  if (fehler instanceof ApiFehler) return fehler.status;
  if (typeof fehler === 'object' && fehler !== null && 'status' in fehler) {
    const s = (fehler as { status: unknown }).status;
    if (typeof s === 'number' && Number.isFinite(s)) return s;
  }
  return null;
}

/**
 * Ein Fehler, den der Aufrufer verursacht hat — Wiederholen ändert nichts.
 *
 * 408 (Zeitüberschreitung) und 429 (zu viele Anfragen) sind ausgenommen: die
 * sind ausdrücklich vorübergehend, dort ist ein zweiter Versuch richtig.
 */
export function istAnfrageFehler(status: number): boolean {
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
}

/**
 * Die Wiederholungsregel des QueryClient.
 *
 * ⚠️ Sie ist eine reine **Verengung** der bisherigen Voreinstellung `retry: 1`.
 * Wo kein Status erkennbar ist (Supabase, Netzabbruch), verhält sie sich
 * byte-identisch wie vorher — nachgelesen in `@tanstack/query-core`
 * (`retryer.js:89`): `failureCount` startet bei **0** und wird erst *nach* der
 * Entscheidung erhöht, `retry: 1` heißt also genau `failureCount < 1`.
 *
 * Neu ist nur: Ein 4xx wird nicht mehr wiederholt. Bis 18.08. verdoppelte
 * `retry: 1` jeden Fehlversuch — bei einer Kachel, die dauerhaft 403 bekommt,
 * also jede Anfrage. Das hielt den Netzverkehr nie still und war damit die
 * halbe Wurzel von BF-3.
 */
export function sollWiederholen(versuche: number, fehler: unknown): boolean {
  const status = apiStatus(fehler);
  if (status !== null && istAnfrageFehler(status)) return false;
  return versuche < 1;
}
